import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeColumnName, type DatabaseField } from '@/lib/column-mapping-utils';
import { toast } from '@/hooks/use-toast';

interface CreateFieldInlineProps {
  defaultLabel: string;
  onCreated: (field: DatabaseField) => void;
  onCancel: () => void;
  targetTable?: 'companies' | 'contacts';
}

export function CreateFieldInline({ defaultLabel, onCreated, onCancel, targetTable = 'companies' }: CreateFieldInlineProps) {
  const [label, setLabel] = useState(defaultLabel);
  const [dataType, setDataType] = useState<string>('text');
  const [creating, setCreating] = useState(false);

  const previewName = sanitizeColumnName(label);

  const handleCreate = async () => {
    if (!label.trim()) {
      toast({ title: 'Field name is required', variant: 'destructive' });
      return;
    }

    try {
      setCreating(true);
      const { data, error } = await supabase.functions.invoke('create-company-field', {
        body: {
          display_label: label.trim(),
          data_type: dataType,
          target_table: targetTable,
        },
      });

      if (error) throw error;

      if (!data.success) {
        toast({ title: 'Failed to create field', description: data.error, variant: 'destructive' });
        return;
      }

      toast({ title: 'Field created', description: `"${data.field.label}" added to ${targetTable} table` });
      onCreated(data.field as DatabaseField);
    } catch (err) {
      console.error('Error creating field:', err);
      toast({
        title: 'Error creating field',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border rounded-lg p-3 bg-blue-50/50 space-y-2 mt-1">
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Field name"
            className="h-8 text-sm"
            disabled={creating}
          />
          <p className="text-[10px] text-muted-foreground mt-0.5 ml-1">
            Column: <code className="bg-muted px-1 rounded">{previewName}</code>
          </p>
        </div>
        <Select value={dataType} onValueChange={setDataType} disabled={creating}>
          <SelectTrigger className="w-[110px] h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="text">Text</SelectItem>
            <SelectItem value="number">Number</SelectItem>
            <SelectItem value="boolean">Yes/No</SelectItem>
            <SelectItem value="date">Date</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          className="h-8 gap-1"
          onClick={handleCreate}
          disabled={creating || !label.trim()}
        >
          {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Create
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={onCancel}
          disabled={creating}
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
